import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Camera, Image as ImageIcon, Trash, Microphone, Stop } from "@phosphor-icons/react";
import { localDateStr } from "@/lib/date";

// Routes where the floating quick-capture button would collide with the page's
// own primary input (e.g. the Assistant's send button, the Search field).
const HIDE_FAB_ROUTES = ["/assistant", "/search"];

export default function QuickCapture() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null); // File
  const [photoPreview, setPhotoPreview] = useState(null); // object URL
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const cameraRef = useRef();
  const galleryRef = useRef();
  const recorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const hidden = HIDE_FAB_ROUTES.some((p) => location.pathname.startsWith(p));

  const startRecording = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice capture isn't supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a format Whisper accepts. Chrome/Firefox → webm/opus by default;
      // iOS Safari → mp4/aac. MediaRecorder chooses the right one.
      const rec = new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) { toast.error("Clip too short — try again"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          const ext = (rec.mimeType && rec.mimeType.includes("mp4")) ? "mp4" : "webm";
          fd.append("file", blob, `voice.${ext}`);
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const t = (data.text || "").trim();
          if (!t) { toast.error("Didn't catch that — try again"); return; }
          setText((prev) => (prev ? `${prev.trim()} ${t}` : t));
        } catch {
          toast.error("Couldn't transcribe — try again in a moment");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Mic permission blocked — enable it in your browser");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview(null);
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const reset = () => { setText(""); clearPhoto(); setOpen(false); };

  const save = async () => {
    if (!text.trim() && !photo) return;
    setBusy(true);
    try {
      const today = localDateStr();
      const { data: log } = await api.get(`/logs/by-date/${today}`);
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/upload?log_id=${log.id}`, fd);
      }
      if (text.trim()) {
        await api.post(`/logs/${log.id}/subentries`, { kind: "opportunity", notes: text.trim() });
      }
      toast.success(photo && text.trim() ? "Photo & note captured" : photo ? "Photo captured" : "Captured");
      reset();
    } catch {
      toast.error("Failed");
    } finally { setBusy(false); }
  };

  if (hidden) return null;

  return (
    <>
      <button data-testid="quick-capture-fab" onClick={() => setOpen(true)} className="fixed bottom-20 md:bottom-8 right-5 z-40 w-14 h-14 rounded-full al-gradient text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform" style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <Plus weight="bold" size={22} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-semibold">
                <img src="/al-axe-mark.svg" alt="" aria-hidden="true" className="h-5 w-auto" /> Quick Capture
              </div>
              <button data-testid="quick-close" onClick={reset}><X size={18} /></button>
            </div>

            <textarea data-testid="quick-text" autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={photoPreview ? 3 : 4} placeholder="An opportunity worth chasing — jot the idea and follow up later…" className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400" />

            {photoPreview && (
              <div className="mt-3 relative rounded-xl overflow-hidden border border-slate-200" data-testid="quick-photo-preview-wrap">
                <img src={photoPreview} alt="preview" className="w-full max-h-64 object-cover" data-testid="quick-photo-preview" />
                <button data-testid="quick-photo-remove" onClick={clearPhoto} className="absolute top-2 right-2 bg-white/95 hover:bg-white rounded-full p-1.5 shadow">
                  <Trash size={14} />
                </button>
              </div>
            )}

            {/* Hidden file inputs — one uses device camera, one is a normal picker */}
            <input ref={cameraRef} data-testid="quick-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} />
            <input ref={galleryRef} data-testid="quick-gallery-input" type="file" accept="image/*" className="hidden" onChange={pickPhoto} />

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button data-testid="quick-camera-btn" onClick={() => cameraRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm hover:bg-slate-50">
                <Camera size={16} weight="duotone" /> Camera
              </button>
              <button data-testid="quick-gallery-btn" onClick={() => galleryRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm hover:bg-slate-50">
                <ImageIcon size={16} weight="duotone" /> Photo
              </button>
              <button
                data-testid="quick-voice-btn"
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors ${recording ? "border-rose-300 bg-rose-50 text-rose-700" : transcribing ? "border-slate-200 text-slate-400" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}
              >
                {recording ? (
                  <><Stop size={16} weight="fill" className="text-rose-600" /> <span className="inline-flex items-center gap-1">Stop<span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" /></span></>
                ) : transcribing ? (
                  <><Microphone size={16} weight="duotone" /> Transcribing…</>
                ) : (
                  <><Microphone size={16} weight="duotone" /> Voice</>
                )}
              </button>
              <div className="flex-1" />
              <button onClick={reset} className="px-4 py-2 text-sm rounded-full border border-slate-200">Cancel</button>
              <button data-testid="quick-save" onClick={save} disabled={busy || (!text.trim() && !photo)} className="al-gradient text-white px-5 py-2 rounded-full text-sm font-medium disabled:opacity-60">
                {busy ? "Saving…" : "Capture"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
